import React from 'react';
import { motion, AnimatePresence } from 'motion/react';

interface PaymentFailureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRetry?: () => void;
  error?: {
    message?: string;
    code?: string;
    decline_code?: string;
    type?: string;
  } | null;
}

const PaymentFailureModal: React.FC<PaymentFailureModalProps> = ({
  isOpen,
  onClose,
  onRetry,
  error,
}) => {
  // Get user-friendly message based on error type
  const getUserFriendlyMessage = () => {
    if (error?.decline_code) {
      const declineCode = error.decline_code.toLowerCase();
      if (declineCode.includes('insufficient_funds') || declineCode.includes('card_declined')) {
        return 'Your payment could not be processed. Please check your card details or try a different payment method.';
      }
      if (declineCode.includes('expired')) {
        return 'Your card has expired. Please use a different payment method.';
      }
      if (declineCode.includes('incorrect')) {
        return 'The card details you entered are incorrect. Please check and try again.';
      }
    }
    return 'We couldn\'t process your payment. Please check your payment details and try again, or contact your bank if the problem persists.';
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-black/90 backdrop-blur-md"
            onClick={onClose}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ 
              type: "spring",
              stiffness: 300,
              damping: 30,
              duration: 0.3
            }}
            className="relative bg-gray-950 border border-gray-800/50 rounded-2xl p-8 max-w-md w-full shadow-2xl"
          >
            {/* Error Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ 
                type: "spring",
                stiffness: 200,
                damping: 15,
                delay: 0.1
              }}
              className="flex justify-center mb-6"
            >
              <div className="w-24 h-24 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center">
                <motion.svg
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ 
                    type: "spring",
                    stiffness: 200,
                    damping: 15,
                    delay: 0.2
                  }}
                  className="w-12 h-12 text-red-400" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </motion.svg>
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-center mb-8"
            >
              <h2 className="text-3xl font-bold text-white mb-3">Payment Failed</h2>
              <p className="text-gray-300 text-base leading-relaxed">
                {getUserFriendlyMessage()}
              </p>
            </motion.div>

            {/* Actions */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-col gap-3"
            >
              {onRetry && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onRetry}
                  className="w-full px-6 py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-colors duration-200 shadow-lg shadow-blue-500/30"
                >
                  Try Again
                </motion.button>
              )}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onClose}
                className="w-full px-6 py-3.5 bg-gray-800/80 hover:bg-gray-800 text-gray-200 rounded-xl font-medium transition-colors duration-200 border border-gray-700/50"
              >
                Close
              </motion.button>
            </motion.div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default PaymentFailureModal;

